import React, { useState } from 'react';
import { BellIcon, FileSearchIcon, HomeIcon, ListChecksIcon } from 'lucide-react';
import type { CitizenScreen } from '../../types/navigation';
import { OWNER_USER } from '../../data/users';
import { PhoneFrame } from '../../components/shared/PhoneFrame';
import { DemoSidePanel } from '../../components/mobile/DemoSidePanel';
import { BottomNav, type NavItem } from '../../components/mobile/BottomNav';
import { CitizenLogin } from './CitizenLogin';
import { CitizenHome } from './CitizenHome';
import { CaseSearch } from './CaseSearch';
import { CitizenCase } from './CitizenCase';
import { DocumentUpload } from './DocumentUpload';
import { PaymentFlow } from './PaymentFlow';
import { ProgressScreen } from './ProgressScreen';
import { Notifications } from './Notifications';
import { ReleaseLetterScreen } from './ReleaseLetterScreen';
export interface CitizenSession {
  name: string;
  mobile: string;
  guestPcr?: string;
}
const NAV: NavItem[] = [{
  id: 'home',
  label: 'হোম',
  icon: HomeIcon
}, {
  id: 'search',
  label: 'PCR অনুসন্ধান',
  icon: FileSearchIcon
}, {
  id: 'progress',
  label: 'অগ্রগতি',
  icon: ListChecksIcon
}, {
  id: 'notifications',
  label: 'বিজ্ঞপ্তি',
  icon: BellIcon
}];
export function CitizenApp({
  onExit
}: {
  onExit: () => void;
}) {
  const [session, setSession] = useState<CitizenSession | null>(null);
  const [screen, setScreen] = useState<CitizenScreen>({
    name: 'home'
  });
  const go = (s: CitizenScreen) => setScreen(s);
  const login = (s: CitizenSession) => {
    setSession(s);
    setScreen(s.guestPcr ? {
      name: 'case',
      pcr: s.guestPcr
    } : {
      name: 'home'
    });
  };
  const side = <DemoSidePanel title="যানবাহন মালিক অ্যাপ / নাগরিক পোর্টাল" roles={[{
    id: 'owner',
    label: 'নিবন্ধিত যানবাহন মালিক',
    name: `${OWNER_USER.name} · OTP লগইন`
  }, {
    id: 'guest',
    label: 'PCR দিয়ে অনুসন্ধান',
    name: 'PCR-2026-00421 · মোবাইল যাচাই'
  }]} active={session ? session.guestPcr ? 'guest' : 'owner' : null} onChange={id => login(id === 'guest' ? {
    name: OWNER_USER.name,
    mobile: OWNER_USER.mobile,
    guestPcr: 'PCR-2026-00421'
  } : {
    name: OWNER_USER.name,
    mobile: OWNER_USER.mobile
  })} hints={['পরিস্থিতি ৩: PCR-2026-00421 খুলুন → কাগজপত্র জমা দিন → জরিমানা পরিশোধ → রসিদ দেখুন।', 'জমা দেওয়ার পর ওয়েব পোর্টালের “কাগজপত্র যাচাই” তালিকায় কেসটি দেখা যাবে।', 'PCR-2026-00424-এ অবমুক্তি পত্র প্রস্তুত — প্রিভিউ, দাপ্তরিক ইস্যু ও হস্তান্তরের পার্থক্য দেখুন।']} data-id="element-1527" data-mp="1pce8sc6gokvl" />;
  if (!session) {
    return <PhoneFrame onExit={onExit} side={side} data-id="element-1528" data-mp="28xxp5af4ecjg"><CitizenLogin onLogin={login} onExit={onExit} data-id="element-1529" data-mp="28wwa6uuzh5wg" /></PhoneFrame>;
  }
  const firstPcr = session.guestPcr ?? 'PCR-2026-00421';
  let body: React.ReactNode;
  switch (screen.name) {
    case 'search':
      body = <CaseSearch session={session} go={go} data-id="element-1530" data-mp="1uyybsyx25k2l" />;
      break;
    case 'case':
      body = <CitizenCase pcr={screen.pcr} go={go} data-id="element-1531" data-mp="1uy6enjntbea7" />;
      break;
    case 'documents':
      body = <DocumentUpload pcr={screen.pcr} session={session} go={go} data-id="element-1532" data-mp="1q16877n1w9ik" />;
      break;
    case 'payment':
      body = <PaymentFlow pcr={screen.pcr} session={session} go={go} data-id="element-1533" data-mp="5grfaggz6q8o" />;
      break;
    case 'progress':
      body = <ProgressScreen pcr={screen.pcr} go={go} data-id="element-1534" data-mp="afuj5xl7yqf2" />;
      break;
    case 'letter':
      body = <ReleaseLetterScreen pcr={screen.pcr} go={go} data-id="element-1535" data-mp="ffgwe711s6nv" />;
      break;
    case 'notifications':
      body = <Notifications session={session} go={go} data-id="element-1536" data-mp="jhbw573bybzj" />;
      break;
    default:
      body = <CitizenHome session={session} go={go} onLogout={() => setSession(null)} data-id="element-1537" data-mp="jfhkkjdy7w84" />;
  }
  return <PhoneFrame onExit={onExit} side={side} data-id="element-1538" data-mp="3iumbd69ixiwg">
      <div className="flex min-h-0 flex-1 flex-col" data-id="element-1539" data-mp="3nvadamzajk5g">{body}</div>
      <BottomNav items={NAV} active={screen.name} onChange={id => go(id === 'progress' ? {
      name: 'progress',
      pcr: firstPcr
    } : {
      name: id
    } as CitizenScreen)} data-id="element-1540" data-mp="3ntfpn0wmxdxo" />
    </PhoneFrame>;
}